import { FinancialConverterError } from '@/utils/errors/converters/financial-converter-errors';

function sanitizeString(str: string) {
  return str.replace(/[^\p{L}\p{N}]/gu, '');
}

function sanitizeNumber(str: string) {
  return str.replace(/[^0-9.,-]/g, '').replace(/,/g, '.');
}

function normalizeFormattedNumber(value: string) {
  return value.replace(/[\u00A0\u202F]/g, ' ').replace(/\u2212/g, '-');
}

interface FormatReadableSvSeNumberOptions {
  allowNegative: boolean;
  maxFractionDigits?: number;
  maxAbsValue?: number;
}

function formatReadableSvSeNumber(
  input: string,
  {
    allowNegative,
    maxFractionDigits,
    maxAbsValue,
  }: FormatReadableSvSeNumberOptions,
) {
  // AI generated function
  if (input.trim() === '') {
    return '';
  }

  const formatter = new Intl.NumberFormat('sv-SE');
  const isNegative = allowNegative && /^\s*-/.test(input);
  const unsignedInput = input.replace(/-/g, '');
  const sanitized = unsignedInput.replace(/[^\d.,]/g, '');

  const decimalMatch = /[.,]/.exec(sanitized);
  const separatorIndex = decimalMatch?.index ?? -1;

  const integerRaw =
    separatorIndex === -1 ? sanitized : sanitized.slice(0, separatorIndex);
  const fractionRaw =
    separatorIndex === -1 ? '' : sanitized.slice(separatorIndex + 1);

  const integerDigits = integerRaw.replace(/\D/g, '');
  const fractionDigitsRaw = fractionRaw.replace(/\D/g, '');
  const safeMaxFractionDigits =
    typeof maxFractionDigits === 'number' && maxFractionDigits >= 0
      ? Math.floor(maxFractionDigits)
      : undefined;
  const fractionDigits =
    safeMaxFractionDigits === undefined
      ? fractionDigitsRaw
      : fractionDigitsRaw.slice(0, safeMaxFractionDigits);
  const hadDecimalSeparator = separatorIndex !== -1;

  if (!integerDigits && !fractionDigits && !hadDecimalSeparator) {
    return isNegative ? '-' : '';
  }

  const groupedInteger = formatter
    .format(Number(integerDigits || '0'))
    .replace(/[\u00A0\u202F]/g, ' ');

  const sign = isNegative ? '-' : '';

  const safeMaxAbsValue =
    typeof maxAbsValue === 'number' && maxAbsValue >= 0
      ? maxAbsValue
      : undefined;
  if (safeMaxAbsValue !== undefined) {
    const numericValue = Number(
      `${sign}${integerDigits || '0'}${hadDecimalSeparator ? `.${fractionDigits}` : ''}`,
    );

    if (!isNaN(numericValue)) {
      const minValue = allowNegative ? -safeMaxAbsValue : 0;
      const clampedValue = Math.min(
        safeMaxAbsValue,
        Math.max(minValue, numericValue),
      );

      if (clampedValue !== numericValue) {
        return normalizeFormattedNumber(
          new Intl.NumberFormat('sv-SE', {
            maximumFractionDigits: safeMaxFractionDigits,
          }).format(clampedValue),
        );
      }
    }
  }

  if (!hadDecimalSeparator || safeMaxFractionDigits === 0) {
    return `${sign}${groupedInteger}`;
  }

  return `${sign}${groupedInteger},${fractionDigits}`;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const createSanitizer = <Format extends (...args: any[]) => any>(args: {
  type: string;
  format: Format;
  regex: RegExp;
}) => {
  return (input: Parameters<Format>[0]): ReturnType<Format> => {
    const formattedInput = args.format ? args.format(input) : input;
    if (args.regex.test(formattedInput)) {
      return formattedInput;
    }
    throw new FinancialConverterError(args.type);
  };
};

class FinancialConverter {
  private applyTransforms<T extends Record<string, unknown>>(
    input: T,
    transforms: Partial<
      Record<keyof ToArgs, (value: string | number) => string | number>
    >,
  ): T {
    const source = input as T & Partial<ToArgs>;
    const result = { ...source } as T & Partial<ToArgs>;

    const transformEntries = Object.entries(transforms) as Array<
      [keyof ToArgs, (value: string | number) => string | number]
    >;

    for (const [key, transform] of transformEntries) {
      if (!(key in source)) {
        continue;
      }

      const value = source[key];
      result[key] = value ? transform(value) : value;
    }

    return result as T;
  }

  strictBIC(bic: string | number) {
    const bicGeneralRegex = /^[A-Z]{6}[A-Z0-9]{2}([A-Z0-9]{3})?$/;
    return createSanitizer({
      type: 'BIC',
      format: (bic) => sanitizeString(String(bic)).toUpperCase(),
      regex: bicGeneralRegex,
    })(bic);
  }

  readableBIC(bic: string | number) {
    const cleanValue = String(bic)
      .replace(/[^a-zA-Z0-9]/g, '')
      .toUpperCase()
      .slice(0, 11);
    const match = cleanValue.match(/^(.{1,4})(.{1,2})?(.{1,2})?(.{1,3})?$/);

    if (!match) return cleanValue;

    // 3. Slice off the full match, remove undefined groups, and join with spaces
    return match.slice(1).filter(Boolean).join(' ');
  }

  strictIBAN(iban: string | number) {
    const ibanGeneralRegex = /^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$/;
    return createSanitizer({
      type: 'IBAN',
      format: (iban) => sanitizeString(String(iban)).toUpperCase(),
      regex: ibanGeneralRegex,
    })(iban);
  }

  readableIBAN(iban: string | number) {
    const rawIban = String(iban)
      .replace(/[^\p{L}\p{N}]/gu, '')
      .toUpperCase();
    const ibanChunks = rawIban.match(/.{1,4}/g);
    return ibanChunks ? ibanChunks.join(' ') : '';
  }

  strictBankgiro(bankgiro: string | number) {
    const bankgiroGeneralRegex = /^\d{7,8}$/;
    return createSanitizer({
      type: 'BankGiro',
      format: (bankgiro) => sanitizeString(String(bankgiro)),
      regex: bankgiroGeneralRegex,
    })(bankgiro);
  }

  readableBankgiro(bankgiro: string | number) {
    const rawBankgiro = String(bankgiro).replace(/\D/g, '');
    const capped = rawBankgiro.slice(0, 8);
    if (capped.length < 4) return capped;

    if (capped.length <= 7) {
      return `${capped.slice(0, 3)}-${capped.slice(3)}`;
    }

    return `${capped.slice(0, 4)}-${capped.slice(4)}`;
  }

  strictPlusgiro(plusgiro: string | number) {
    const plusgiroGeneralRegex = /^\d{2,8}$/;
    return createSanitizer({
      type: 'PlusGiro',
      format: (plusgiro) => sanitizeString(String(plusgiro)),
      regex: plusgiroGeneralRegex,
    })(plusgiro);
  }

  readablePlusgiro(plusgiro: string | number) {
    const rawPlusgiro = String(plusgiro).replace(/\D/g, '').slice(0, 8);
    if (rawPlusgiro.length < 2) {
      return rawPlusgiro;
    }

    return `${rawPlusgiro.slice(0, -1)}-${rawPlusgiro.slice(-1)}`;
  }

  strictOrgNr(orgNr: string | number) {
    // Loose to make sure orgNr from other countries can be stored
    return createSanitizer({
      type: 'OrganisationNumber',
      format: (orgNr) => sanitizeString(String(orgNr)).toUpperCase(),
      regex: /^[A-Z0-9]{5,15}$/,
    })(orgNr);
  }

  readableOrgNr(orgNr: string | number) {
    const raw = String(orgNr)
      .replace(/[^\p{L}\p{N}]/gu, '')
      .toUpperCase();
    const capped = raw.slice(0, 15);
    if (capped.length <= 6) return capped;
    // Swedish format, the one most likely to be used, is 6 digits, a dash and then 4 digits (or more for companies)
    return `${capped.slice(0, 6)}-${capped.slice(6)}`;
  }

  strictZipCode(zipCode: string | number) {
    return sanitizeString(String(zipCode));
  }

  readableZipCode(zipCode: string | number) {
    const raw = String(zipCode).replace(/\D/g, '').slice(0, 5);
    if (raw.length <= 3) return raw;
    return `${raw.slice(0, 3)} ${raw.slice(3)}`;
  }

  strictNumber(number: string | number) {
    const num = Number(sanitizeNumber(String(number)));

    if (isNaN(num)) {
      throw new FinancialConverterError('Number');
    }
    return num;
  }

  readableNumber(number: string | number, maxFractionDigits?: number) {
    return formatReadableSvSeNumber(String(number), {
      allowNegative: true,
      maxFractionDigits,
    });
  }

  strictPositiveNumber(number: string | number) {
    const num = Number(sanitizeNumber(String(number)));

    if (isNaN(num) || num < 0) {
      throw new FinancialConverterError('PositiveNumber');
    }
    return num;
  }

  readablePositiveNumber(number: string | number, maxFractionDigits?: number) {
    return formatReadableSvSeNumber(String(number), {
      allowNegative: false,
      maxFractionDigits,
    });
  }

  strictPennyAdjust(number: string | number) {
    const num = Number(sanitizeNumber(String(number)));

    if (isNaN(num)) {
      throw new FinancialConverterError('PennyAdjust');
    }
    return Number(num.toFixed(2));
  }

  readablePennyAdjust(number: string | number) {
    return formatReadableSvSeNumber(String(number), {
      allowNegative: true,
      maxFractionDigits: 2,
    });
  }

  strictVatAmount(number: string | number) {
    const num = Number(sanitizeNumber(String(number)));

    if (isNaN(num)) {
      throw new FinancialConverterError('VatAmount');
    }
    return Number(num.toFixed(2));
  }

  readableVatAmount(number: string | number) {
    return formatReadableSvSeNumber(String(number), {
      allowNegative: true,
      maxFractionDigits: 2,
    });
  }

  strictTotal(number: string | number) {
    const num = Number(sanitizeNumber(String(number)));

    if (isNaN(num)) {
      throw new FinancialConverterError('Total');
    }
    return Number(num.toFixed(2));
  }

  readableTotal(number: string | number) {
    return formatReadableSvSeNumber(String(number), {
      allowNegative: true,
      maxFractionDigits: 2,
    });
  }

  toReadable<T extends Record<string, unknown>>(input: T): T {
    return this.applyTransforms(input, {
      bankGiro: (value) => this.readableBankgiro(value),
      postGiro: (value) => this.readablePlusgiro(value),
      plusGiro: (value) => this.readablePlusgiro(value),
      iban: (value) => this.readableIBAN(value),
      organisationNumber: (value) => this.readableOrgNr(value),
      zipCode: (value) => this.readableZipCode(value),
      bic: (value) => this.readableBIC(value),
      total: (value) => this.readableTotal(value),
      pennyAdjust: (value) => this.readablePennyAdjust(value),
      vat: (value) => this.readableVatAmount(value),
    });
  }

  toStrict<T extends Record<string, unknown>>(input: T): T {
    return this.applyTransforms(input, {
      bankGiro: (value) => this.strictBankgiro(value),
      postGiro: (value) => this.strictPlusgiro(value),
      plusGiro: (value) => this.strictPlusgiro(value),
      iban: (value) => this.strictIBAN(value),
      organisationNumber: (value) => this.strictOrgNr(value),
      zipCode: (value) => this.strictZipCode(value),
      bic: (value) => this.strictBIC(value),
      total: (value) => this.strictTotal(value),
      pennyAdjust: (value) => this.strictPennyAdjust(value),
      vat: (value) => this.strictVatAmount(value),
    });
  }
}

type ToArgs = {
  iban?: string | number | null;
  bankGiro?: string | number | null;
  postGiro?: string | number | null;
  plusGiro?: string | number | null;
  organisationNumber?: string | number | null;
  zipCode?: string | number | null;
  bic?: string | number | null;
  total?: string | number | null;
  vat?: string | number | null;
  pennyAdjust?: string | number | null;
};

export const financialConverter = new FinancialConverter();
